import React, { CSSProperties } from 'react'
import { isEmpty } from 'lodash'
import styled from '@emotion/styled'
import { Locale, Translation } from 'locale'
import { PendingValidationCell, ValidationStatus } from 'view/shared/upload'
import * as constants from 'view/styles/constants'
import { SpreadsheetParseValidationResult } from 'view/shared/allocationDialogs/fundAssignmentDialog/upload/validateTradingEntityAssignmentParseResult'

export interface PendingValidationPanelProps<T = unknown> {
  panelHeading: string
  validationResults: SpreadsheetParseValidationResult<T>[]
  columnDefinitions: ValidationPanelColDef<T>[]
  getRowId: (r: any) => string
}

export const PendingContainer = styled.div`
  height: 100%;
  margin-bottom: 20px;

  h4 {
    margin-bottom: 10px;
  }

  button {
    margin-left: auto;
  }
`

const PendingScrollContainer = styled.div`
  height: calc(100% - 30px);
  overflow-y: auto;
  padding-right: 5px;
  margin-bottom: 20px;
`

const PendingTable = styled.table`
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  thead {
    th {
      text-align: left;
      position: sticky;
      top: 0px;
      z-index: 2;
      background: ${constants.CORE_PRIMARY};
      font-weight: normal;

      font-size: 13px;
      padding: 5px;

      :first-child {
        padding-left: 15px;
      }
    }
  }
`

export interface ValidationPanelColDef<T> {
  headerLabel: Translation
  getter: (rowData: T) => string | number | null
  fieldName: string
  formatter?: (value: any) => string
  style?: CSSProperties
}

export const PendingValidationPanel = <T,>({
  columnDefinitions,
  validationResults,
  panelHeading,
  getRowId
}: PendingValidationPanelProps<T>) => (
  <PendingContainer data-test-id="pending-validation-panel">
    <h4>{panelHeading}</h4>
    <PendingScrollContainer>
      <PendingTable>
        <thead>
          <tr>
            {columnDefinitions.map(colDef => (
              <th key={colDef.headerLabel}>{Locale.translate(colDef.headerLabel)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {validationResults.map(({ item, validationIssues }) => (
            <tr key={getRowId(item)}>
              {columnDefinitions.map(colDef => (
                <PendingValidationCell
                  style={colDef.style}
                  dataTestId={`col-${colDef.fieldName}`}
                  key={colDef.fieldName}
                  fieldName={colDef.fieldName}
                  rowValidationStatus={isEmpty(validationIssues) ? ValidationStatus.DEFAULT : ValidationStatus.ERROR}
                  validationIssues={validationIssues}>
                  {(colDef.formatter ?? (v => v))(colDef.getter(item))}
                </PendingValidationCell>
              ))}
            </tr>
          ))}
        </tbody>
      </PendingTable>
    </PendingScrollContainer>
  </PendingContainer>
)
